import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { landingPagesService } from '@/services/landingPagesService';
import ReadinessTable from '@/components/landing/ReadinessTable';

/**
 * Which clients can have a landing page built, and what each one is missing.
 *
 * ⚠ THIS ANSWER ALREADY EXISTED AND WAS INVISIBLE. Every build computes a
 * `defaulted` list naming the values it could not source from the client's
 * brand, the API has always returned it, and this file is the first thing to
 * render it. Until now a page assembled from a wordmark, a fallback palette
 * and no proof looked exactly as finished as one built from a real brand, and
 * the only way to discover a prerequisite was missing was to notice something
 * absent from a rendering.
 *
 * ⚠ "NOT CHECKED" IS NOT "MISSING". The form column reads each client's live
 * site, which takes about forty seconds across the roster, so the grid loads
 * without it. An unchecked cell says so rather than showing a cross, because
 * a readiness screen that guesses is worse than one that admits it does not
 * know.
 */




export default function ReadinessPanel({ tenantId }: { tenantId?: string }) {
  const [withForms, setWithForms] = useState(false);
  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['landing-readiness', withForms],
    queryFn: () => landingPagesService.readiness(undefined, withForms),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    // ⚠ A LINE OF TEXT IS NOT A LOADING STATE FOR A SLOW SCREEN. This reads
    // every client on the roster, and the deployed backend took 34 seconds
    // before the queries were batched — a single grey sentence for that long
    // reads as a page that failed. The skeleton shows the shape of what is
    // coming and says what is being waited on, so the wait is legible.
    return (
      <div className="space-y-4" aria-busy="true" aria-live="polite">
        <p className="text-sm text-teal-dark">
          Checking every client&rsquo;s brand record
          {withForms ? ' and reading their live site for a lead form' : ''}…
        </p>
        <div className="overflow-hidden rounded-md border border-border">
          <div className="flex gap-4 border-b border-border bg-teal-light px-3 py-2">
            {['Client', 'Logo', 'Palette', 'Form', 'Ads', 'Falls back on'].map((h) => (
              <span
                key={h}
                className={`text-sm font-medium ${h === 'Client' ? 'w-56' : h === 'Falls back on' ? 'flex-1' : 'w-16 text-center'}`}
              >
                {h}
              </span>
            ))}
          </div>
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="flex animate-pulse items-center gap-4 border-b border-border px-3 py-3 last:border-b-0"
            >
              <span className="w-56 space-y-1">
                <span className="block h-3 w-40 rounded bg-muted" />
                <span className="block h-2 w-28 rounded bg-muted/70" />
              </span>
              {[0, 1, 2, 3].map((c) => (
                <span key={c} className="w-16 text-center">
                  <span className="mx-auto block h-3 w-3 rounded-full bg-muted" />
                </span>
              ))}
              <span className="flex flex-1 gap-1">
                <span className="h-4 w-20 rounded bg-muted" />
                <span className="h-4 w-16 rounded bg-muted/70" />
              </span>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {withForms
            ? 'Reading live websites takes about forty seconds across the roster.'
            : 'This reads the brand record for every client, not just the one selected.'}
        </p>
      </div>
    );
  }
  if (isError) {
    return (
      <p className="text-sm text-destructive">
        Could not load readiness: {String((error as Error)?.message)}
      </p>
    );
  }
  if (!data) return null;

  const rows = [...data.items].sort((a, b) => {
    if (a.tenant_id === tenantId) return -1;
    if (b.tenant_id === tenantId) return 1;
    return a.tenant_id.localeCompare(b.tenant_id);
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {/* ⚠ THREE ROWS MUST NOT READ AS THE WHOLE ROSTER. A member sees only
            the clients they belong to, and saying "3 clients" flat would have
            them believe the agency has three. */}
        <p className="text-sm text-teal-dark">
          <span className="font-medium tabular-nums">{data.total}</span>{' '}
          {data.scope === 'member' ? 'of your clients' : 'clients'}
          {data.forms_checked > 0 && (
            <>
              , <span className="font-medium tabular-nums">{data.buildable}</span>{' '}
              ready to build
            </>
          )}
        </p>
        <label className="ml-auto flex cursor-pointer items-center gap-2 text-sm text-teal-dark">
          <input
            type="checkbox"
            checked={withForms}
            onChange={(e) => setWithForms(e.target.checked)}
          />
          Also check lead forms
          <span className="text-xs text-muted-foreground">
            (reads every client&apos;s site, ~40s)
          </span>
        </label>
        {isFetching && (
          <span className="text-xs text-muted-foreground">refreshing…</span>
        )}
      </div>

      <ReadinessTable rows={rows} tenantId={tenantId} />

      <p className="text-xs text-muted-foreground">
        Ads means a Google Ads credential resolves for this client, which is what
        puts the campaign picker on the Builder tab. It does not mean campaigns
        are running. A client with no ads still builds a page from a typed
        service name.
      </p>
    </div>
  );
}
